"use client";

import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Image from "next/image";
import NeighborhoodsCard from "./NeighborhoodsCard";
import { optimizeImageUrl } from "@/app/utils/optimizeImageUrl";
import { useContext, useEffect, useRef, useState } from "react";

import { screenSizeContext } from "@/app/store/context/screenSizesContext";

gsap.registerPlugin(ScrollTrigger);

const lerp = (x: number, y: number, a: number) => (1 - a) * x + a * y;

const NeighborhoodsComponent = ({ data }: any) => {
	const { isIpad, isReady } = useContext(screenSizeContext);
	const containerRef = useRef<HTMLDivElement>(null);
	const [gallery, setGallery] = useState<{ src: string; alt: string }[]>();
	const [info, setInfo] = useState<{ label: string; value: string }[]>();
// console.log(data,'asdsadasd')
	useEffect(() => {
		setGallery(() => {
			const images: { src: string; alt: string }[] = [];
			data?.gallery?.map((item: any) => {
				images.push({
					src: process.env.NEXT_PUBLIC_STATIC_MEDIA_API_URL + item.media_name,
					alt: "gallery",
				});
			});
			return images;
		});

		setInfo([
			...(Number(data?.starting_price) === 0
				? []
				: [
						{
							label: "Starting Price",
							value: data?.starting_price,
						},
				  ]),
			{
				label: "Unit Types ",
				value: data?.unit_types[0]?.name?.en,
			},
			{
				label: "SQM",
				value: data?.property_sizes?.en,
			},

			...(data?.bathrooms_no
				? [
					{
						label: "Number of Bathrooms",
						value: data?.bathrooms_no,
					},
				]
				: []),
				...(data?.bedrooms_no?.en
				? [
					{
						label: "Number of Bedrooms",
						value: data?.bedrooms_no?.en,
					},
				]
				: []),

		]);
	}, [data]);
	
	useEffect(()=> {
		// Wait for viewport — desktop branch gsap.set(logo, y:105%) must not run on mobile first.
		if (!isReady || !containerRef.current) return;

		const query = gsap.utils.selector(containerRef.current);
	
		/* Animated Elements */
		const cols = query(".neighborhoods-col");
		const content = query(".neighborhoods_content");
		const logo = query(".neighborhoods_circle_Logo div");
		const link = query(".neighborhoods_circle_line a");
		const logoAndLink = [...logo, ...link].filter(Boolean);
	
		let splitText: any;
		if (!isIpad) {
			gsap.set([cols, content], { y: "105vh" });
			if (logoAndLink.length > 0) {
				gsap.set(logoAndLink, { y: "105%" });
			}

			return () => {
				splitText?.revert();
				gsap.set([cols, content, ...logoAndLink].filter(Boolean), {
					clearProps: "transform",
				});
			};
		} else {
			const logoHead = query(".neighborhoods_circle_container");
			const neighborhoodsCircle = document.querySelector(".neighborhoods-intro .neighborhoods-circle");
	
			const duration = 1.2;

			// Clear any desktop-path transforms so the circle logo stays visible.
			if (logoAndLink.length > 0) {
				gsap.set(logoAndLink, { clearProps: "transform" });
			}
	
			gsap.set(cols, { y: "105%" });
			gsap.set(content, { y: "105%" });
	
			const intro = gsap.to(logoHead, { paused: true, opacity: 0, duration: 1 });
			const scaleUp = gsap.to(neighborhoodsCircle, { paused: true, scale: 1.75, duration: 1 });
			const scaleDown = gsap.to(neighborhoodsCircle, { paused: true, scale: 1, duration: 1 });
	
			const pinTrigger = ScrollTrigger.create({
				trigger: containerRef.current,
				start: "top-=200px top",
				end: "bottom-=17% top",
				pin: logoHead,
				pinSpacing: false,
				anticipatePin: 0.3,
				onUpdate: ({ progress })=> {
					intro.progress(lerp(0, 1, Math.min(0.4, progress) / 0.4));
					if (progress > 0.65) scaleDown.progress((progress - 0.65) / 0.35); 
					else scaleUp.progress(Math.min(0.65, progress) / 0.65); 
				}
			});
	
			const trigger = ScrollTrigger.create({
				trigger: containerRef.current,
				start: "top bottom-=20%",
				end: "top bottom-=20%",
				once: true,
				onEnter: ()=> {
					gsap.to(cols, {
						duration,
						stagger: 0.05,
						y: "0%",
						ease: "power4.out",
					});
					gsap.to(content, {
						duration,
						y: "0%",
						ease: "power4.out",
					});
				},
			});
	
			return () => {
				intro.kill();
				scaleUp.kill();
				scaleDown.kill();
				pinTrigger.kill();
				trigger.kill();
				gsap.set([cols, content, ...logoAndLink].filter(Boolean), {
					clearProps: "transform",
				});
			};
		}
	}, [data, isIpad, isReady])

	return (
		<div
			ref={containerRef}
			className={`neighborhoods || w-full min-h-screen relative px lg:absolute lg:top-0 lg:left-0 lg:px-0 lg:pointer-events-none lg:overflow-hidden`}
		>
			<div
				className={`neighborhoods_circle_container || h-[min(25.3125rem,100vw)] flex items-center justify-center opacity-100 lg:block lg:h-auto lg:absolute lg:top-[50vh] lg:left-[50%] lg:transform lg:-translate-x-1/2 lg:-translate-y-1/2`}
			>
				{/* Below lg: box height = the O's container width, so when both pin at the same top the logo sits at the O's centre */}
				<div className="w-full h-full relative flex items-center justify-center lg:block">
					<div className="neighborhoods_circle_content || relative lg:absolute lg:top-1/2 lg:left-1/2 lg:transform lg:-translate-x-1/2 lg:-translate-y-1/4">
						<div className="neighborhoods_circle_Logo mb-[2.45rem] w-[11.7rem] overflow-hidden">
							<div className="flex items-center justify-center">
								{data?.logo_image?.media_name && (
									<Image
										src={optimizeImageUrl(
											`${process.env.NEXT_PUBLIC_STATIC_MEDIA_API_URL}${data?.logo_image?.media_name}`,
											{ width: 200, quality: 80 }
										)}
										alt="logo"
										width={200}
										height={200}
										className="object-contain"
									/>
								)}
							</div>
						</div>
						{data?.brochure?.media_name && (
							<div className="neighborhoods_circle_line || overflow-hidden">
								<a
									target="_blank"
									href={
										process.env.NEXT_PUBLIC_STATIC_MEDIA_API_URL +
										data?.brochure?.media_name
									}
									className="cursor-pointer flex align_center justify-center w-full text-center text-oBlue-100 underline uppercase text-labelS leading-[1.05rem] tracking-[0.105rem] font-semibold"
								>
									Download
									<br />
									brochure
								</a>
							</div>
						)}
					</div>
				</div>
			</div>
			<NeighborhoodsCard
				data={{ ...data, gallery, info }}
				noStartingPrice={
					Number(data?.starting_price) === 0 ||
					data?.starting_price === "-" ||
					data?.starting_price === null
				}
			/>
		</div>
	);
};

export default NeighborhoodsComponent;
